import { type ItemId, isItemId } from '@/data/items';
import type { Inventory } from '@/world/inventory';

/**
 * The bag: quick access, separate from the backpack.
 *
 * The backpack is storage and can grow large. The bag is the handful of things
 * the thumb can reach without opening a menu, and it is deliberately small — a
 * quick-access row you have to scroll is not quick access.
 *
 * A bag slot holds an *assignment*, not an item. Slot 3 means "turnip seeds",
 * and how many you have is answered by the backpack every time it is asked.
 * Holding the items themselves would mean two containers to keep in step, and
 * a harvest that landed in the backpack while a stack of the same crop sat in
 * the bag would leave the player with two piles they could not combine.
 *
 * Assignments outlive their stock. Running out of seeds empties the shelf, not
 * the label: the slot stays assigned and greys out, so a loadout survives a
 * planting session rather than quietly unravelling as things are used up.
 */

/** Slots on the bar. Small on purpose; see above. */
export const BAG_SIZE = 8;

export class QuickBag {
  private readonly slots: (ItemId | null)[];
  private selected = 0;

  constructor(slots: readonly (ItemId | null)[] = []) {
    this.slots = Array.from({ length: BAG_SIZE }, (_, i) => slots[i] ?? null);
  }

  /** What a new farm starts with to hand. */
  static starting(): QuickBag {
    return new QuickBag(['bait:worm', 'seed:turnip', 'feed']);
  }

  get selectedIndex(): number {
    return this.selected;
  }

  select(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= BAG_SIZE) return;
    this.selected = index;
  }

  /** Move the selection by a signed amount, wrapping at both ends. */
  cycle(delta: number): void {
    this.selected = (this.selected + delta + BAG_SIZE) % BAG_SIZE;
  }

  /** The item assigned to a slot, stock or no stock. */
  assignmentAt(index: number): ItemId | null {
    return this.slots[index] ?? null;
  }

  /** Put an item on the bar. Assigning null clears the slot. */
  assign(index: number, id: ItemId | null): boolean {
    if (!Number.isInteger(index) || index < 0 || index >= BAG_SIZE) return false;
    // One slot per item: an item on the bar twice is two labels for one pile,
    // and picking either would draw down the other.
    if (id !== null) {
      const existing = this.slots.indexOf(id);
      if (existing !== -1 && existing !== index) this.slots[existing] = null;
    }
    this.slots[index] = id;
    return true;
  }

  /** The assigned item, or null when the slot is empty or out of stock. */
  itemAt(index: number, backpack: Inventory): ItemId | null {
    const id = this.assignmentAt(index);
    if (!id) return null;
    return backpack.countOf(id) > 0 ? id : null;
  }

  /** What is actually in hand: the selected assignment, if any is left. */
  heldItem(backpack: Inventory): ItemId | null {
    return this.itemAt(this.selected, backpack);
  }

  /** How many of the selected slot's item remain. */
  countAt(index: number, backpack: Inventory): number {
    const id = this.assignmentAt(index);
    return id ? backpack.countOf(id) : 0;
  }

  /**
   * Spend from the held slot. Returns false when there was not enough, so a
   * caller cannot plant a seed it never had.
   */
  consumeHeld(backpack: Inventory, count = 1): boolean {
    const id = this.heldItem(backpack);
    if (!id || backpack.countOf(id) < count) return false;
    return backpack.remove(id, count) === count;
  }

  toSave(): (string | null)[] {
    return [...this.slots];
  }

  static fromSave(value: unknown, selected: unknown): QuickBag {
    const entries = Array.isArray(value) ? value : [];
    const bag = new QuickBag(
      entries.map((entry) => (isItemId(entry) ? entry : null)),
    );
    if (typeof selected === 'number') bag.select(selected);
    return bag;
  }
}
